import React, { useState, useRef, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Upload, X, CheckCircle2, AlertCircle, Loader2, Folder, ChevronDown } from 'lucide-react';
import { V, labelStyle, h2Style, primaryBtnStyle } from '../vividGlass';
import brandAssetActions from '../../../../actions/brandAssets';
import { ASSET_TYPES_CONFIG } from './assetConstants';

const PURPOSES = [
  { value: 'brand_identity', label: 'Brand' },
  { value: 'inspiration', label: 'Reference' },
];

const MAX_BYTES = 20 * 1024 * 1024;
let _uid = 0;

export default function AssetUploadModal({ brandId, folderId, folders = [], onClose, onDone }) {
  const [items, setItems] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState(folderId || null);
  const [folderDropOpen, setFolderDropOpen] = useState(false);
  const fileRef = useRef();

  const addFiles = useCallback((fileList) => {
    const arr = Array.from(fileList).filter((f) => f.type.startsWith('image/'));
    setItems((prev) => [
      ...prev,
      ...arr.map((file) => {
        const tooLarge = file.size > MAX_BYTES;
        return {
          id: ++_uid,
          file,
          previewUrl: URL.createObjectURL(file),
          type: 'auto',
          purpose: 'brand_identity',
          status: tooLarge ? 'error' : 'pending',
          errorMsg: tooLarge ? 'File too large — max 20 MB' : null,
        };
      }),
    ]);
  }, []);

  const removeItem = (id) =>
    setItems((prev) => {
      const it = prev.find((i) => i.id === id);
      if (it?.previewUrl) URL.revokeObjectURL(it.previewUrl);
      return prev.filter((i) => i.id !== id);
    });

  const updateItem = (id, patch) =>
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));

  const handleUpload = async () => {
    const pending = items.filter((i) => i.status === 'pending');
    if (!pending.length) return;
    setUploading(true);
    let errorCount = 0;
    for (const item of pending) {
      updateItem(item.id, { status: 'uploading', errorMsg: null });
      try {
        await brandAssetActions.uploadAndCreateAsset(brandId, item.file, {
          asset_type: item.type === 'auto' ? 'other' : item.type,
          usage_type: item.purpose,
          folder_id: selectedFolderId || null,
        });
        updateItem(item.id, { status: 'done' });
      } catch (err) {
        errorCount += 1;
        updateItem(item.id, { status: 'error', errorMsg: err?.message || 'Upload failed' });
      }
    }
    setUploading(false);
    if (errorCount === 0) {
      setTimeout(onDone, 700);
    }
  };

  const doneCount = items.filter((i) => i.status === 'done').length;
  const pendingCount = items.filter((i) => i.status === 'pending').length;
  const errorCount = items.filter((i) => i.status === 'error').length;

  const selectedFolder = folders.find((f) => f.folder_id === selectedFolderId);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: V.font }}>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={!uploading ? onClose : undefined}
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(14px)' }}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
        style={{
          position: 'relative', width: 'min(92vw, 1020px)', maxHeight: '88vh',
          background: 'rgba(8,3,18,0.98)', borderRadius: 24,
          border: `1px solid ${V.glassBorder}`, backdropFilter: 'blur(24px)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 32px 96px rgba(0,0,0,0.7)',
        }}
      >
        {/* Header */}
        <div style={{ padding: '20px 24px 18px', borderBottom: `1px solid rgba(255,255,255,0.07)`, display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
          <div style={{ flex: 1 }}>
            <h2 style={{ ...h2Style, marginBottom: items.length > 0 ? 4 : 0 }}>Upload Assets</h2>
            {items.length > 0 && (
              <div style={{ fontSize: 12, color: V.textMuted }}>
                {items.length} file{items.length !== 1 ? 's' : ''} selected{doneCount > 0 ? ` · ${doneCount} uploaded` : ''}
              </div>
            )}
          </div>
          {items.length > 0 && !uploading && (
            <span style={{ fontSize: 12, color: V.textMuted }}>Types auto-detected during captioning</span>
          )}
          <button type="button" onClick={!uploading ? onClose : undefined}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: V.textMuted, display: 'flex', padding: 4 }}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
          {items.length === 0 ? (
            <DropZone dragOver={dragOver} setDragOver={setDragOver} onFiles={addFiles} fileRef={fileRef} />
          ) : (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
                <button type="button" onClick={() => fileRef.current?.click()}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'transparent', border: `1px dashed ${V.glassBorder}`, borderRadius: 10, color: V.textMuted, fontSize: 13, cursor: 'pointer', fontFamily: V.font, flexShrink: 0 }}>
                  <Upload size={12} /> Add more
                </button>
                <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={(e) => addFiles(e.target.files)} />
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
                  style={{ flex: 1, padding: '7px 14px', border: `1px dashed ${dragOver ? V.primary : 'transparent'}`, borderRadius: 10, color: V.textMuted, fontSize: 12, background: dragOver ? `${V.primary}10` : 'transparent', textAlign: 'center', transition: 'all 0.15s' }}
                >
                  {dragOver ? '↓ Drop to add' : 'or drag more files here'}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(175px, 1fr))', gap: 16 }}>
                {items.map((item) => (
                  <FileCard
                    key={item.id}
                    item={item}
                    onRemove={() => removeItem(item.id)}
                    onType={(t) => updateItem(item.id, { type: t })}
                    onPurpose={(p) => updateItem(item.id, { purpose: p })}
                    locked={item.status !== 'pending'}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div style={{ padding: '14px 24px', borderTop: `1px solid rgba(255,255,255,0.07)`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, background: 'rgba(0,0,0,0.3)', gap: 12 }}>
            {/* Folder selector */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <button type="button" onClick={() => !uploading && setFolderDropOpen((v) => !v)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 14px', background: 'rgba(255,255,255,0.05)', border: `1px solid ${V.glassBorder}`, borderRadius: 10, color: selectedFolderId ? V.textSub : V.textMuted, fontSize: 13, cursor: 'pointer', fontFamily: V.font, maxWidth: 200 }}>
                <Folder size={13} color={selectedFolderId ? V.primary : V.textMuted} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {selectedFolderId ? (selectedFolder?.name || 'Folder') : 'No folder'}
                </span>
                <ChevronDown size={11} style={{ flexShrink: 0, opacity: 0.5 }} />
              </button>
              {folderDropOpen && (
                <div style={{ position: 'absolute', bottom: 'calc(100% + 8px)', left: 0, minWidth: 190, background: 'rgba(8,3,18,0.98)', border: `1px solid ${V.glassBorder}`, borderRadius: 14, padding: 8, boxShadow: '0 16px 48px rgba(0,0,0,0.7)', zIndex: 10 }}>
                  <button type="button" onClick={() => { setSelectedFolderId(null); setFolderDropOpen(false); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 10px', background: !selectedFolderId ? `${V.primary}15` : 'transparent', border: 'none', borderRadius: 8, color: !selectedFolderId ? V.primary : V.textSub, fontSize: 13, cursor: 'pointer', fontFamily: V.font, textAlign: 'left' }}>
                    <Folder size={12} /> Unassigned
                  </button>
                  {folders.map((f) => (
                    <button type="button" key={f.folder_id} onClick={() => { setSelectedFolderId(f.folder_id); setFolderDropOpen(false); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 10px', background: selectedFolderId === f.folder_id ? `${V.primary}15` : 'transparent', border: 'none', borderRadius: 8, color: selectedFolderId === f.folder_id ? V.primary : V.textSub, fontSize: 13, cursor: 'pointer', fontFamily: V.font, textAlign: 'left' }}>
                      <Folder size={12} color={V.textMuted} /> {f.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 'auto' }}>
              <span style={{ fontSize: 13, color: V.textMuted }}>
                {uploading ? `Uploading ${doneCount} of ${items.length}…` : `${pendingCount} asset${pendingCount !== 1 ? 's' : ''} ready`}
              </span>
              <button type="button" onClick={!uploading ? onClose : undefined}
                style={{ padding: '9px 20px', background: 'transparent', border: `1px solid ${V.glassBorder}`, borderRadius: 12, color: V.textSub, fontSize: 13, cursor: 'pointer', fontFamily: V.font }}>
                Cancel
              </button>
              <button type="button" onClick={handleUpload}
                disabled={uploading || pendingCount === 0 || errorCount > 0}
                style={{
                  ...primaryBtnStyle,
                  padding: '9px 24px', fontSize: 13,
                  opacity: uploading || pendingCount === 0 || errorCount > 0 ? 0.45 : 1,
                  cursor: uploading || pendingCount === 0 || errorCount > 0 ? 'default' : 'pointer',
                }}>
                {uploading
                  ? <><Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} /> Uploading…</>
                  : errorCount > 0
                    ? `Remove ${errorCount} failed file${errorCount !== 1 ? 's' : ''}`
                    : `Upload ${pendingCount} asset${pendingCount !== 1 ? 's' : ''} →`}
              </button>
            </div>
          </div>
        )}
        <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      </motion.div>
    </div>
  );
}

function DropZone({ dragOver, setDragOver, onFiles, fileRef }) {
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); onFiles(e.dataTransfer.files); }}
      onClick={() => fileRef.current?.click()}
      style={{
        border: `2px dashed ${dragOver ? V.primary : V.glassBorder}`,
        borderRadius: 20, minHeight: 320,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 20, cursor: 'pointer',
        background: dragOver ? `${V.primary}08` : 'rgba(255,255,255,0.02)',
        transition: 'all 0.15s',
      }}
    >
      <div style={{ width: 80, height: 80, borderRadius: 99, background: `${V.primary}15`, border: `1px solid ${V.primary}30`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Upload size={32} color={V.primary} />
      </div>
      <div style={{ textAlign: 'center' }}>
        <div style={{ color: V.text, fontSize: 20, fontWeight: 700, marginBottom: 8, fontFamily: V.font }}>
          {dragOver ? 'Drop to add' : 'Drop images here'}
        </div>
        <div style={{ color: V.textMuted, fontSize: 14, fontFamily: V.font }}>
          or <span style={{ color: V.primary }}>browse files</span> — PNG, JPG, WEBP, multiple OK
        </div>
      </div>
      <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={(e) => onFiles(e.target.files)} />
    </div>
  );
}

function FileCard({ item, onRemove, onType, onPurpose, locked }) {
  const [typeOpen, setTypeOpen] = useState(false);
  const typeConf = ASSET_TYPES_CONFIG.find((t) => t.value === item.type) || ASSET_TYPES_CONFIG.at(-1);

  return (
    <div style={{
      borderRadius: 16,
      background: 'rgba(255,255,255,0.04)',
      border: `1px solid ${item.status === 'done' ? 'rgba(74,222,128,0.4)' : item.status === 'error' ? 'rgba(245,87,108,0.4)' : V.glassBorder}`,
      position: 'relative', transition: 'border-color 0.15s',
    }}>
      {/* Thumbnail */}
      <div style={{ position: 'relative', aspectRatio: '4/3', borderRadius: '16px 16px 0 0', overflow: 'hidden', background: 'rgba(0,0,0,0.3)' }}>
        <img src={item.previewUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        {item.status === 'uploading' && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Loader2 size={26} color="#fff" style={{ animation: 'spin 0.8s linear infinite' }} />
          </div>
        )}
        {item.status === 'done' && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CheckCircle2 size={32} color="#4ade80" />
          </div>
        )}
        {item.status === 'error' && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(245,87,108,0.2)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, padding: 8 }}>
            <AlertCircle size={26} color="#f5576c" />
            {item.errorMsg && <span style={{ fontSize: 10, color: '#f5576c', textAlign: 'center', lineHeight: 1.3, maxWidth: '90%', fontWeight: 600 }}>{item.errorMsg}</span>}
          </div>
        )}
        {!locked && (
          <button type="button" onClick={onRemove}
            style={{ position: 'absolute', top: 7, right: 7, width: 24, height: 24, borderRadius: 99, background: 'rgba(0,0,0,0.75)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
            <X size={11} />
          </button>
        )}
      </div>

      {/* Info */}
      <div style={{ padding: '10px 12px 12px' }}>
        <div style={{ fontSize: 11, color: V.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 8, fontFamily: V.font }} title={item.file.name}>
          {item.file.name}
        </div>

        {/* Type chip */}
        <div style={{ position: 'relative', marginBottom: 6 }}>
          <button type="button" onClick={() => !locked && setTypeOpen((v) => !v)}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '5px 10px', borderRadius: 99, background: `${typeConf.color}22`, border: `1px solid ${typeConf.color}55`, color: typeConf.color, fontSize: 11, fontWeight: 600, cursor: locked ? 'default' : 'pointer', fontFamily: V.font, boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: typeConf.color, display: 'inline-block', flexShrink: 0 }} />
              {typeConf.label}
            </div>
            {!locked && <ChevronDown size={10} style={{ opacity: 0.6, flexShrink: 0 }} />}
          </button>

          {typeOpen && (
            <div style={{ position: 'absolute', bottom: 'calc(100% + 6px)', left: 0, right: 0, background: 'rgba(8,3,18,0.98)', border: `1px solid ${V.glassBorder}`, borderRadius: 12, padding: 8, zIndex: 100, boxShadow: '0 16px 48px rgba(0,0,0,0.7)', display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {ASSET_TYPES_CONFIG.filter((t) => t.value !== 'auto').map((t) => (
                <button type="button" key={t.value} onClick={() => { onType(t.value); setTypeOpen(false); }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 9px', borderRadius: 99, background: item.type === t.value ? `${t.color}28` : 'transparent', border: `1px solid ${item.type === t.value ? t.color + '55' : 'transparent'}`, color: item.type === t.value ? t.color : V.textMuted, fontSize: 11, cursor: 'pointer', fontFamily: V.font, whiteSpace: 'nowrap' }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: t.color, display: 'inline-block' }} />
                  {t.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Purpose toggle */}
        <div style={{ display: 'flex', gap: 4 }}>
          {PURPOSES.map((p) => (
            <button type="button" key={p.value} onClick={() => !locked && onPurpose(p.value)}
              style={{ flex: 1, padding: '4px 0', borderRadius: 8, background: item.purpose === p.value ? 'rgba(255,255,255,0.10)' : 'transparent', border: `1px solid ${item.purpose === p.value ? V.glassBorderHover : V.glassBorder}`, color: item.purpose === p.value ? V.text : V.textMuted, fontSize: 10, fontWeight: item.purpose === p.value ? 700 : 400, cursor: locked ? 'default' : 'pointer', fontFamily: V.font, transition: 'all 0.15s' }}>
              {p.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
